import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CheckPlan.module.scss';
import {
  IconPlus,
  IconDots,
  IconChevronUp,
  IconChevronDown,
  IconTrash,
  IconFilterFilled,
  IconSearch,
  IconX,
  IconExternalLink
} from '@tabler/icons-react';
import {ProjectDto, CheckPointDto, TaskDto, Status, LabelDto, Methods} from '../../api/model';
import CheckpointDetailModal from './CheckpointDetailModal';
import CreateQualityCheckPlanModal, { QualityCheckPlanData } from './CreateQualityCheckPlanModal';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import CreateActionPointModal from '../ActionPoints/CreateActionPointModal';
import { usePostActionpoint, getGetActionpointProjectIdProjectQueryKey } from '../../api/action-point-api/action-point-api';
import { useDeleteCheckpointId, useGetCheckpointProjectProjectId } from '../../api/check-point-api/check-point-api';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '../../hooks/useNotifications';
import { usePutCheckpointUpdate } from '../../hooks/usePutCheckpointUpdate';
import { useProjectTasks } from '../../hooks/useProjectTasks';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import { useProjectSprints } from '../../hooks/useProjectSprints';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { useGetRoleManagementRoles } from '../../api/role-management-api/role-management-api';
import DeliverableDetailPopup from '../Deliverables/components/DeliverableDetailPopup';
import {isAgileArtefactDeliverable} from '../../utils/phaseUtils';

interface CheckPlanProps {
  project?: ProjectDto;
}

interface CheckpointRowData {
  id: string;
  taskId: number;
  checkpointId: number;
  taskName: string;
  taskDescription: string;
  status?: Status;
  validationDate?: string;
  actualValidationDate?: string;
  responsibleRole?: string;
  validationCriteria?: string;
  acceptanceCriteria?: string;
  validationMethod?: string;
  checkpoint?: CheckPointDto;
  task: TaskDto;
  deliverObjectId?: number;
  deliverObjectName?: string;
}

interface DeliverableSection {
  id: string;
  deliverableName: string;
  deliverableId: number | null;
  sprintName?: string;
  items: CheckpointRowData[];
}

const CheckPlan: React.FC<CheckPlanProps> = ({ project }) => {
  const { t } = useTranslation('checkPlan');

  const hasSprintsProject = project?.method === Methods.AGILE || project?.method === Methods.HYBRID;
  const buildBaseTabs = (extra: string[] = []): string[] =>
    hasSprintsProject ? ['Proof Plan Point', 'Sprint Plan', ...extra] : ['Proof Plan Point', ...extra];

  // Helper function to get display name for tabs
  const getTabDisplayName = (tabName: string): string => {
    if (tabName === 'Proof Plan Point') return t('tabs.proofPlanPoint');
    if (tabName === 'Sprint Plan') return t('tabs.sprintPlan');
    return tabName; // Return custom plan names as-is
  };
  // Fetch
  const {
    tasks: allTasks,
    isLoading: isLoadingTasks
  } = useProjectTasks(project);

  const {
    deliverables: allDeliverables,
    isLoading: isLoadingDeliverables
  } = useProjectDeliverables(project);

  const hasInitializedExpansion = useRef(false);
  const tabsWrapperRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('checkPlanItemsPerPage');
    return saved ? parseInt(saved, 10) || 8 : 8;
  });
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<CheckpointRowData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState<any>(null);
  const [isDeliverablePopupOpen, setIsDeliverablePopupOpen] = useState(false);
  const [isQualityPlanModalOpen, setIsQualityPlanModalOpen] = useState(false);
  const [customPlans, setCustomPlans] = useState<Map<string, QualityCheckPlanData>>(new Map());
  const [tabs, setTabs] = useState<string[]>(() => buildBaseTabs());
  const [activeTab, setActiveTab] = useState(0);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [checkpointToDelete, setCheckpointToDelete] = useState<CheckPointDto | null>(null);
  const [isStatusChangeDialogOpen, setIsStatusChangeDialogOpen] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState<{checkpoint: CheckpointRowData, newStatus: Status} | null>(null);
  const [isDeleteTabDialogOpen, setIsDeleteTabDialogOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<{index: number, name: string} | null>(null);
  const [checkpointForActionPoint, setCheckpointForActionPoint] = useState<CheckpointRowData | null>(null);

  // Filter states
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [deliverableNameFilter, setDeliverableNameFilter] = useState('');
  const [taskNameFilter, setTaskNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [responsibleRoleFilter, setResponsibleRoleFilter] = useState('');
  const [assignedPersonFilter, setAssignedPersonFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState<number[]>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
  const [sprintFilter, setSprintFilter] = useState<number[]>([]);
  const [isSprintDropdownOpen, setIsSprintDropdownOpen] = useState(false);

  // Reference for dropdown click outside handling
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const { sprints: allSprints } = useProjectSprints(project?.id);
  const { namesByIds: assigneeNames } = useTenantUsers();
  const { data: tenantRoles } = useGetRoleManagementRoles({ query: { staleTime: 5 * 60 * 1000, retry: false } });

  // matrix role key -> display name for the responsible-role column
  const roleNameByKey = useMemo(() => {
    const m = new Map<string, string>();
    (tenantRoles ?? []).forEach(r => { if (r.roleKey) m.set(r.roleKey, r.displayName || r.roleKey); });
    return m;
  }, [tenantRoles]);

  // Hooks
  const { notifySuccess, notifyError } = useNotifications();
  const { mutateAsync: deleteCheckpoint } = useDeleteCheckpointId();
  const { mutateAsync: updateCheckpoint } = usePutCheckpointUpdate();
  const queryClient = useQueryClient();

  // Load custom plans
  useEffect(() => {
    if (!project?.id) return;
    const storageKey = `checkPlans_${project.id}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const plansMap = new Map<string, QualityCheckPlanData>(Object.entries(parsed));
        setCustomPlans(plansMap);
        setTabs(buildBaseTabs(Array.from(plansMap.keys())));
      } catch (e) {
        console.error('Failed to load custom plans:', e);
      }
    }
  }, [project?.id]);

  // Purge stale plans when deliverables are loaded (handles reused project IDs after deletion)
  useEffect(() => {
    if (!project?.id || !allDeliverables || allDeliverables.length === 0 || customPlans.size === 0) return;
    const validIds = new Set(allDeliverables.map(d => d.id).filter(Boolean));
    const staleNames: string[] = [];
    customPlans.forEach((plan, name) => {
      if (!plan.selectedDeliverableIds.some(id => validIds.has(id))) {
        staleNames.push(name);
      }
    });
    if (staleNames.length === 0) return;
    const newPlans = new Map(customPlans);
    staleNames.forEach(name => newPlans.delete(name));
    setCustomPlans(newPlans);
    const storageKey = `checkPlans_${project.id}`;
    localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(newPlans)));
    setTabs(buildBaseTabs(Array.from(newPlans.keys())));
    setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, allDeliverables]);

  // sync plan tab
  useEffect(() => {
    setTabs(prev => {
      const hasSprintTab = prev.includes('Sprint Plan');
      if (hasSprintsProject === hasSprintTab) return prev;
      if (hasSprintsProject) {
        const idx = prev.indexOf('Proof Plan Point');
        const next = [...prev];
        next.splice(idx + 1, 0, 'Sprint Plan');
        return next;
      }
      setActiveTab(0);
      return prev.filter(tab => tab !== 'Sprint Plan');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSprintsProject]);

  // Save custom plans to local storage
  const saveCustomPlans = (plans: Map<string, QualityCheckPlanData>) => {
    if (!project?.id) return;
    const storageKey = `checkPlans_${project.id}`;
    const obj = Object.fromEntries(plans);
    localStorage.setItem(storageKey, JSON.stringify(obj));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowDropdown(null);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  // if click outside close filterpanel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
          filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target as Node)
      ) {
        setIsFilterPanelOpen(false);
        setIsStatusDropdownOpen(false);
        setIsLabelDropdownOpen(false);
        setIsSprintDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // handle toggle status filter
  const handleStatusFilterChange = (status: Status) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(item => item !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleLabelFilterChange = (labelId: number) => {
    setLabelFilter(prev =>
        prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]
    );
  };

  const handleSprintFilterChange = (sprintId: number) => {
    setSprintFilter(prev =>
        prev.includes(sprintId) ? prev.filter(id => id !== sprintId) : [...prev, sprintId]
    );
  };

  const formatDate = (localDate?: any): string => {
    if (!localDate) return 'N/A';
    try {
      const date = new Date(localDate);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status?: Status): 'success' | 'warning' | 'error' => {
    if (!status) return 'warning';
    switch (status.toString().toUpperCase()) {
      case 'COMPLETED':
      case 'DONE':
        return 'success';
      case 'IN_PROGRESS':
      case 'ONGOING':
        return 'warning';
      case 'NOT_STARTED':
      case 'TODO':
        return 'error';
      default:
        return 'warning';
    }
  };

  // Get project tasks
  const projectTasks = useMemo(() => {
    if (!allTasks || !project) return [];
    return allTasks.filter(task => task.id !== undefined);
  }, [allTasks, project]);

  // Create deliverable map
  const deliverableMap = useMemo(() => {
    const map = new Map<number, string>();
    if (allDeliverables) {
      allDeliverables.forEach(deliverable => {
        if (deliverable.id !== undefined && deliverable.name) {
          map.set(deliverable.id, deliverable.name);
        }
      });
    }
    return map;
  }, [allDeliverables]);

  // Map deliverable id → sprint id
  const deliverableSprintMap = useMemo(() => {
    const map = new Map<number, number>();
    if (allDeliverables) {
      allDeliverables.forEach(deliverable => {
        const sprint = (deliverable as any).sprint;
        if (deliverable.id !== undefined && sprint) {
          map.set(deliverable.id, sprint);
        }
      });
    }
    return map;
  }, [allDeliverables]);

  // Map deliverable id → labels
  const deliverableLabelMap = useMemo(() => {
    const map = new Map<number, LabelDto[]>();
    if (allDeliverables) {
      allDeliverables.forEach(deliverable => {
        if (deliverable.id !== undefined && (deliverable as any).labels) {
          map.set(deliverable.id, (deliverable as any).labels);
        }
      });
    }
    return map;
  }, [allDeliverables]);

  // All unique labels across deliverables
  const allLabels = useMemo(() => {
    const seen = new Map<number, LabelDto>();
    deliverableLabelMap.forEach(labels => {
      labels.forEach(label => {
        if (label.id !== undefined) seen.set(label.id, label);
      });
    });
    return Array.from(seen.values());
  }, [deliverableLabelMap]);

  // Map sprint id → sprint name
  const sprintNameMap = useMemo(() => {
    const map = new Map<number, string>();
    allSprints.forEach(sprint => {
      if (sprint.id && sprint.name) {
        map.set(sprint.id, sprint.name);
      }
    });
    return map;
  }, [allSprints]);

  // All project checkpoints in one call
  const {
    data: allCheckpoints,
    isLoading: isLoadingCheckpoints,
  } = useGetCheckpointProjectProjectId(project?.id ?? 0, {
    query: { enabled: !!project?.id },
  });

  // Fast taskId to checkpoint lookup
  const checkpointByTaskId = useMemo(() => {
    const map = new Map<number, CheckPointDto | undefined>();
    (allCheckpoints ?? []).forEach(cp => {
      if (cp.taskId) map.set(cp.taskId, cp);
    });
    return map;
  }, [allCheckpoints]);

  const tableData = useMemo((): DeliverableSection[] => {
    if (isLoadingTasks || isLoadingDeliverables || isLoadingCheckpoints || projectTasks.length === 0) return [];

    // Get filter for current tab
    const currentTabName = tabs[activeTab];
    const isBuiltInTab = currentTabName === 'Proof Plan Point' || currentTabName === 'Sprint Plan';
    const currentPlan = isBuiltInTab ? null : customPlans.get(currentTabName);
    const filterDeliverableIds = currentPlan ? new Set(currentPlan.selectedDeliverableIds) : null;
    const isSprintPlanTab = currentTabName === 'Sprint Plan';
    const isProofPlanTab = currentTabName === 'Proof Plan Point';

    // Group checkpoints by sprint (if exists) or by deliverable
    const groupItems = new Map<string, CheckpointRowData[]>();
    const groupMeta = new Map<string, { deliverableName: string; deliverableId: number | null; sprintName?: string }>();

    projectTasks.forEach((task) => {
      const taskId = task.id;
      if (!taskId) return;

      const deliverObjectId = task.deliverObjectId ?? null;

      // Filter by deliverable ids if applicable
      if (filterDeliverableIds && deliverObjectId && !filterDeliverableIds.has(deliverObjectId)) {
        return;
      }

      const checkpoint = checkpointByTaskId.get(taskId);

      const deliverObjectName = deliverObjectId ? deliverableMap.get(deliverObjectId) : undefined;

      const checkpointData: CheckpointRowData = {
        id: `checkpoint-${taskId}`,
        taskId,
        checkpointId: checkpoint?.id || 0,
        taskName: task.name || t('states.unnamedTask'),
        taskDescription: task.description || t('states.noDescription'),
        status: checkpoint?.status,
        validationDate: formatDate(checkpoint?.validationDate),
        actualValidationDate: formatDate(checkpoint?.actualValidationDate),
        responsibleRole: checkpoint?.responsibleRole,
        validationCriteria: checkpoint?.validationCriteria,
        acceptanceCriteria: checkpoint?.acceptanceCriteria,
        validationMethod: checkpoint?.validationMethod,
        checkpoint,
        task,
        deliverObjectId: deliverObjectId ?? undefined,
        deliverObjectName,
      };

      // Determine group: "Agile Artefact" deliverables by sprint, all others by deliverable
      const sprintId = deliverObjectId ? deliverableSprintMap.get(deliverObjectId) : undefined;
      const deliverableNameStr = deliverObjectId ? deliverableMap.get(deliverObjectId) : undefined;
        const isAgileArtefact = isAgileArtefactDeliverable({name: deliverableNameStr});
      const groupKey = (isAgileArtefact && sprintId) ? `sprint-${sprintId}` : `deliverable-${deliverObjectId ?? 'none'}`;

      if (isProofPlanTab && groupKey.startsWith('sprint-')) return;
      if (isSprintPlanTab && !groupKey.startsWith('sprint-')) return;

      if (!groupItems.has(groupKey)) {
        groupItems.set(groupKey, []);
        const sprintName = (isAgileArtefact && sprintId) ? sprintNameMap.get(sprintId) : undefined;
        const deliverableName = deliverObjectId
            ? (deliverableMap.get(deliverObjectId) || `Deliverable ${deliverObjectId}`)
            : t('states.noDeliverable');
        groupMeta.set(groupKey, {
          deliverableName: sprintName ?? deliverableName,
          deliverableId: deliverObjectId,
          sprintName,
        });
      }
      groupItems.get(groupKey)!.push(checkpointData);
    });

    const sections: DeliverableSection[] = [];
    groupItems.forEach((items, groupKey) => {
      const meta = groupMeta.get(groupKey)!;

      // Sort items by validationDate ascending
      const sortedItems = [...items].sort((a, b) => {
        if (!a.checkpoint?.validationDate && !b.checkpoint?.validationDate) return 0;
        if (!a.checkpoint?.validationDate) return 1;
        if (!b.checkpoint?.validationDate) return -1;
        return new Date(a.checkpoint.validationDate as any).getTime() - new Date(b.checkpoint.validationDate as any).getTime();
      });

      sections.push({
        id: groupKey,
        deliverableName: meta.deliverableName,
        deliverableId: meta.deliverableId,
        sprintName: meta.sprintName,
        items: sortedItems,
      });
    });

    // Sort sections by earliest validation date, undated last
    const getEarliestTime = (section: DeliverableSection): number | null => {
      const date = section.items[0]?.checkpoint?.validationDate;
      if (!date) return null;
      const time = new Date(date as any).getTime();
      return isNaN(time) ? null : time;
    };

    return sections.sort((a, b) => {
      const aTime = getEarliestTime(a);
      const bTime = getEarliestTime(b);
      if (aTime === null && bTime === null) return a.deliverableName.localeCompare(b.deliverableName);
      if (aTime === null) return 1;
      if (bTime === null) return -1;
      if (aTime !== bTime) return aTime - bTime;
      return a.deliverableName.localeCompare(b.deliverableName);
    });
  }, [projectTasks, checkpointByTaskId, isLoadingTasks, isLoadingDeliverables, isLoadingCheckpoints, deliverableMap, deliverableSprintMap, sprintNameMap, activeTab, tabs, customPlans]);

  // apply the filters
  const filteredTableData = useMemo((): DeliverableSection[] => {
    if (!tableData.length) return [];

    return tableData.map(section => {
      // Filter by deliverable name
      const deliverableNameMatch = !deliverableNameFilter ||
        section.deliverableName.toLowerCase().includes(deliverableNameFilter.toLowerCase());

      if (!deliverableNameMatch) return null;

      // Sprint filter: only show sprint sections matching selected sprints
      if (sprintFilter.length > 0 && section.id.startsWith('sprint-')) {
        const sectionSprintId = parseInt(section.id.replace('sprint-', ''), 10);
        if (!sprintFilter.includes(sectionSprintId)) return null;
      }

      // filtered item handling
      const filteredItems = section.items.filter(item => {
        const taskNameMatch = !taskNameFilter ||
          item.taskName.toLowerCase().includes(taskNameFilter.toLowerCase());

        // Status filter
        const statusMatch = statusFilter.length === 0 ||
          (item.status && statusFilter.includes(item.status));

        // Responsible role filter
        const responsibleRoleText = item.responsibleRole
          ? (roleNameByKey.get(item.responsibleRole) || item.responsibleRole)
          : '';
        const responsibleRoleMatch = !responsibleRoleFilter ||
          responsibleRoleText.toLowerCase().includes(responsibleRoleFilter.toLowerCase());

        // Assigned person filter
        const assignedName = item.task?.assignedUserIds?.length ? assigneeNames(item.task.assignedUserIds) : '';
        const assignedPersonMatch = !assignedPersonFilter ||
            assignedName.toLowerCase().includes(assignedPersonFilter.toLowerCase());

        // Label filter (via deliverable)
        const labelMatch = labelFilter.length === 0 || (() => {
          if (!item.deliverObjectId) return false;
          const labels = deliverableLabelMap.get(item.deliverObjectId) ?? [];
          return labelFilter.some(id => labels.some(l => l.id === id));
        })();

        return taskNameMatch && statusMatch && responsibleRoleMatch && assignedPersonMatch && labelMatch;
      });

      // Only return section if it has matching items
      return filteredItems.length > 0 ? {
        ...section,
        items: filteredItems
      } : null;
    }).filter((section): section is DeliverableSection => section !== null);
  }, [tableData, deliverableNameFilter, taskNameFilter, statusFilter, responsibleRoleFilter, assignedPersonFilter, labelFilter, sprintFilter, deliverableLabelMap, assigneeNames, roleNameByKey]);

  // Sprints that actually appear as section headers (for the sprint filter dropdown)
  const availableSprints = useMemo(() => {
    const sprintIds = new Set<number>();
    tableData.forEach(section => {
      if (section.id.startsWith('sprint-')) {
        const id = parseInt(section.id.replace('sprint-', ''), 10);
        if (!isNaN(id)) sprintIds.add(id);
      }
    });
    return allSprints.filter(s => s.id !== undefined && sprintIds.has(s.id!));
  }, [tableData, allSprints]);

  const totalPages = Math.max(1, Math.ceil(filteredTableData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSectionData = filteredTableData.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  // Reset state when project changes (like anything else)
  useEffect(() => {
    hasInitializedExpansion.current = false;
    setExpandedSections([]);
    setSprintFilter([]);
    setSmartExpandDone(false);
  }, [project?.id]);

  // as soon as sections are available
  useEffect(() => {
    if (tableData.length > 0 && !hasInitializedExpansion.current) {
      setExpandedSections([tableData[0].id]);
      hasInitializedExpansion.current = true;
    }
  }, [tableData]);

  // expand sections with a checkpoint
  const [smartExpandDone, setSmartExpandDone] = useState(false);
  useEffect(() => {
    if (smartExpandDone || isLoadingCheckpoints || !hasInitializedExpansion.current || tableData.length === 0) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today);
    cutoff.setDate(today.getDate() + 7);
    const sectionsToExpand = tableData
      .filter(section => section.items.some(item => {
        if (item.status === Status.FINISHED) return false;
        if (!item.checkpoint?.validationDate) return false;
        const d = new Date(item.checkpoint.validationDate as any);
        return !isNaN(d.getTime()) && d >= today && d <= cutoff;
      }))
      .map(s => s.id);
    if (sectionsToExpand.length > 0) {
      setExpandedSections(prev => Array.from(new Set([...prev, ...sectionsToExpand])));
    }
    setSmartExpandDone(true);
  }, [tableData, isLoadingCheckpoints, smartExpandDone]);

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleAddNewTab = () => setIsQualityPlanModalOpen(true);
  const handleOptionsClick = () => console.log('Options clicked');

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    // Scroll tab into view
    setTimeout(() => {
      if (tabsWrapperRef.current) {
        const activeTabElement = tabsWrapperRef.current.querySelector(`[data-tab-index="${index}"]`);
        if (activeTabElement) {
          activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    }, 0);
  };

  const handleCreateQualityPlan = (planData: QualityCheckPlanData) => {
    const newPlans = new Map(customPlans);
    newPlans.set(planData.designation, planData);
    setCustomPlans(newPlans);
    saveCustomPlans(newPlans);
    setTabs([...tabs, planData.designation]);
    setActiveTab(tabs.length);
    notifySuccess(t('notifications.planCreated'), t('notifications.planCreatedMsg', { name: planData.designation }));
  };

  const handleDeleteTab = (index: number, tabName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // built-in tabs cannot be deleted
    if (index === 0 || index === 1) return;
    setTabToDelete({ index, name: tabName });
    setIsDeleteTabDialogOpen(true);
  };

  const confirmDeleteTab = () => {
    if (!tabToDelete) return;

    const { index, name } = tabToDelete;

    const newPlans = new Map(customPlans);
    newPlans.delete(name);
    setCustomPlans(newPlans);
    saveCustomPlans(newPlans);

    const newTabs = tabs.filter((_, i) => i !== index);
    setTabs(newTabs);

    if (activeTab === index) {
      setActiveTab(0);
    } else if (activeTab > index) {
      setActiveTab(activeTab - 1);
    }

    setTabToDelete(null);
    notifySuccess(t('notifications.planDeleted'), t('notifications.planDeletedMsg', { name }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    localStorage.setItem('checkPlanItemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleRowOptionsClick = (data: CheckpointRowData, event: React.MouseEvent) => {
    event.stopPropagation();
    setShowDropdown(showDropdown === data.id ? null : data.id);
  };

  const handleDeleteClick = (data: CheckpointRowData) => {
    if (data.checkpoint) {
      setCheckpointToDelete(data.checkpoint);
      setIsDeleteDialogOpen(true);
      setShowDropdown(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!checkpointToDelete?.id) return;

    try {
      await deleteCheckpoint({ id: checkpointToDelete.id });
      notifySuccess(t('notifications.checkpointDeleted'), t('notifications.checkpointDeletedMsg'));

      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/checkpoint');
        },
        refetchType: 'active',
      });

      setCheckpointToDelete(null);
    } catch (error) {
      notifyError(t('notifications.checkpointDeleted'), error);
    }
  };
  // deal with valuation dot clicks
  const handleStatusDotClick = (checkpoint: CheckpointRowData, newStatus: Status, e: React.MouseEvent) => {
    e.stopPropagation();
    setStatusChangeData({ checkpoint, newStatus });
    setIsStatusChangeDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!statusChangeData?.checkpoint?.checkpoint?.id) return;

    try {
      const updateData: CheckPointDto = {
        ...statusChangeData.checkpoint.checkpoint,
        status: statusChangeData.newStatus,
      };

      await updateCheckpoint({
        id: statusChangeData.checkpoint.checkpoint.id,
        data: updateData
      });

      notifySuccess(t('notifications.updateStatus'), t('notifications.updateStatusMsg', { status: statusChangeData.newStatus }));

      // Immediately refetch all active checkpoint queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/checkpoint');
        },
        refetchType: 'active',
      });

      setStatusChangeData(null);
    } catch (error) {
      notifyError(t('notifications.updateStatus'), error);
    }
  };
  const handleViewDetails = (data: CheckpointRowData) => {
    if (isDetailModalOpen) return;
    setSelectedCheckpoint(data);
    setIsDetailModalOpen(true);
  };

  const handleCheckpointClick = (data: CheckpointRowData) => {
    handleViewDetails(data);
  };
  const handleSaveCheckpoint = (updated: any) => {
    setIsDetailModalOpen(false);
  };


  // Include checkpoints so first paint has correct sort
  const isLoading = isLoadingTasks || isLoadingDeliverables || isLoadingCheckpoints;

  return (
    <>
      <div className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('header.title')}</h2>
          <p className={styles.planDescription}>
            {t('header.description')}
          </p>
        </div>
        <div className={styles.planActions}>
          <button
            className={`${styles.filterButton} ${isFilterPanelOpen ? styles.active : ''} ${
                (deliverableNameFilter || taskNameFilter || statusFilter.length > 0 || responsibleRoleFilter || assignedPersonFilter || labelFilter.length > 0 || sprintFilter.length > 0) ? styles.filtersApplied : ''
            }`}
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            aria-label="Filter checkpoints"
          >
            <IconFilterFilled />
            <span className={styles.filterButtonText}>{t('filterButtons.filter')}</span>
            {(deliverableNameFilter || taskNameFilter || statusFilter.length > 0 || responsibleRoleFilter || assignedPersonFilter || labelFilter.length > 0 || sprintFilter.length > 0) &&
                <span className={styles.filterBadge}></span>}
          </button>

          {isFilterPanelOpen && (
            <div className={styles.filterPanel} ref={filterPanelRef}>
              <div className={styles.filterSearchWrapper}>
                <div className={styles.searchInput}>
                  <IconSearch size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder={t('placeholders.deliverable')}
                    value={deliverableNameFilter}
                    onChange={(e) => setDeliverableNameFilter(e.target.value)}
                    className={styles.filterInput}
                  />
                  {deliverableNameFilter && (
                    <button
                      className={styles.clearButton}
                      onClick={() => setDeliverableNameFilter('')}
                      aria-label="Clear deliverable name search"
                    >
                      <IconX size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.filterSearchWrapper}>
                <div className={styles.searchInput}>
                  <IconSearch size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder={t('placeholders.task')}
                    value={taskNameFilter}
                    onChange={(e) => setTaskNameFilter(e.target.value)}
                    className={styles.filterInput}
                  />
                  {taskNameFilter && (
                    <button
                      className={styles.clearButton}
                      onClick={() => setTaskNameFilter('')}
                      aria-label="Clear task name search"
                    >
                      <IconX size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.filterSearchWrapper}>
                <div className={styles.searchInput}>
                  <IconSearch size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder={t('placeholders.responsibleRole')}
                    value={responsibleRoleFilter}
                    onChange={(e) => setResponsibleRoleFilter(e.target.value)}
                    className={styles.filterInput}
                  />
                  {responsibleRoleFilter && (
                    <button
                      className={styles.clearButton}
                      onClick={() => setResponsibleRoleFilter('')}
                      aria-label="Clear responsible role search"
                    >
                      <IconX size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.filterSearchWrapper}>
                <div className={styles.searchInput}>
                  <IconSearch size={16} className={styles.searchIcon}/>
                  <input
                      type="text"
                      placeholder={t('placeholders.assignedPerson')}
                      value={assignedPersonFilter}
                      onChange={(e) => setAssignedPersonFilter(e.target.value)}
                      className={styles.filterInput}
                  />
                  {assignedPersonFilter && (
                      <button
                          className={styles.clearButton}
                          onClick={() => setAssignedPersonFilter('')}
                          aria-label="Clear assigned person search"
                      >
                        <IconX size={14}/>
                      </button>
                  )}
                </div>
              </div>

              {allLabels.length > 0 && (
                  <div className={styles.filterCriticalityWrapper}>
                    <div className={styles.dropdownContainer}>
                      <button
                          className={styles.dropdownButton}
                          onClick={() => setIsLabelDropdownOpen(!isLabelDropdownOpen)}
                      >
                        {labelFilter.length > 0
                            ? t(labelFilter.length === 1 ? 'filterButtons.labelCountSingular' : 'filterButtons.labelCountPlural', { count: labelFilter.length })
                            : t('filterButtons.label')}
                        <IconChevronDown size={16}
                                         className={`${styles.dropdownIcon} ${isLabelDropdownOpen ? styles.rotate : ''}`}/>
                      </button>
                      {isLabelDropdownOpen && (
                          <div className={styles.dropdown}>
                            {allLabels.map(label => (
                                <div key={label.id} className={styles.dropdownItem}>
                                  <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={labelFilter.includes(label.id!)}
                                        onChange={() => handleLabelFilterChange(label.id!)}
                                        className={styles.checkbox}
                                    />
                                    {label.color && (
                                        <span
                                            className={styles.criticalityDot}
                                            style={{backgroundColor: label.color}}
                                        />
                                    )}
                                    {label.name}
                                  </label>
                                </div>
                            ))}
                          </div>
                      )}
                    </div>
                  </div>
              )}

              {availableSprints.length > 0 && (
                  <div className={styles.filterCriticalityWrapper}>
                    <div className={styles.dropdownContainer}>
                      <button
                          className={styles.dropdownButton}
                          onClick={() => setIsSprintDropdownOpen(!isSprintDropdownOpen)}
                      >
                        {sprintFilter.length > 0
                            ? t(sprintFilter.length === 1 ? 'filterButtons.sprintCountSingular' : 'filterButtons.sprintCountPlural', { count: sprintFilter.length })
                            : t('filterButtons.sprint')}
                        <IconChevronDown size={16}
                                         className={`${styles.dropdownIcon} ${isSprintDropdownOpen ? styles.rotate : ''}`}/>
                      </button>
                      {isSprintDropdownOpen && (
                          <div className={styles.dropdown}>
                            {availableSprints.map(sprint => (
                                <div key={sprint.id} className={styles.dropdownItem}>
                                  <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={sprintFilter.includes(sprint.id!)}
                                        onChange={() => handleSprintFilterChange(sprint.id!)}
                                        className={styles.checkbox}
                                    />
                                    {sprint.name}
                                  </label>
                                </div>
                            ))}
                          </div>
                      )}
                    </div>
                  </div>
              )}

              <div className={styles.filterCriticalityWrapper}>
                <div className={styles.dropdownContainer}>
                  <button
                      className={styles.dropdownButton}
                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                  >
                    {statusFilter.length > 0
                        ? t(statusFilter.length === 1 ? 'filterButtons.statusCountSingular' : 'filterButtons.statusCountPlural', { count: statusFilter.length })
                      : t('filterButtons.valuationStatus')}
                    <IconChevronDown size={16} className={`${styles.dropdownIcon} ${isStatusDropdownOpen ? styles.rotate : ''}`} />
                  </button>

                  {isStatusDropdownOpen && (
                    <div className={styles.dropdown}>
                      <div className={styles.dropdownItem}>
                        <label className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(Status.FINISHED)}
                            onChange={() => handleStatusFilterChange(Status.FINISHED)}
                            className={styles.checkbox}
                          />
                          <span className={`${styles.criticalityDot} ${styles.finished}`}></span>
                          {t('checkpointDetail.statusOptions.finished')}
                        </label>
                      </div>
                      <div className={styles.dropdownItem}>
                        <label className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(Status.PLANNED)}
                            onChange={() => handleStatusFilterChange(Status.PLANNED)}
                            className={styles.checkbox}
                          />
                          <span className={`${styles.criticalityDot} ${styles.planned}`}></span>
                          {t('checkpointDetail.statusOptions.planned')}
                        </label>
                      </div>
                      <div className={styles.dropdownItem}>
                        <label className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(Status.DELAYED)}
                            onChange={() => handleStatusFilterChange(Status.DELAYED)}
                            className={styles.checkbox}
                          />
                          <span className={`${styles.criticalityDot} ${styles.delayed}`}></span>
                          {t('checkpointDetail.statusOptions.delayed')}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <div className={styles.tabsContainer}>
          <div className={styles.tabsWrapper} ref={tabsWrapperRef}>
            {tabs.map((tab, index) => (
              <div key={index} data-tab-index={index} className={`${styles.tabButton} ${activeTab === index ? styles.activeTab : ''}`} onClick={() => handleTabClick(index)}>
                <span className={styles.tabText} title={getTabDisplayName(tab)}>{getTabDisplayName(tab)}</span>
                {index > 1 && (
                  <button
                    className={styles.deleteTabButton}
                    onClick={(e) => handleDeleteTab(index, tab, e)}
                    title={t('header.deleteTabTitle')}
                  >
                    <IconTrash size={14} />
                  </button>
                )}
              </div>
            ))}
            <div className={styles.addTab} onClick={handleAddNewTab} title={t('header.addNewTab')}>
              <IconPlus className={styles.iconSvg} />
            </div>
            <div className={styles.optionsWrapper} onClick={handleOptionsClick} title="More Options">
              <IconDots />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableContent}>
          <div className={styles.tableHeader}>
            <div className={styles.idColumn}><div className={`${styles.headerCell} ${styles.center}`}><span className={styles.headerText}>{t('table.checkpointId')}</span></div></div>
            <div className={styles.checkColumn}><div className={`${styles.headerCell} ${styles.center}`}><span className={styles.headerText}>{t('table.taskId')}</span></div></div>
            <div className={styles.descriptionColumn}><div className={styles.headerCell}><span className={styles.headerText}>{t('table.taskName')}</span></div></div>
            <div className={styles.deliveryColumn}><div className={styles.headerCell}><span className={styles.headerText}>{t('table.validationCriteria')}</span></div></div>
            <div className={styles.deliveryColumn}><div className={styles.headerCell}><span className={styles.headerText}>{t('table.acceptanceCriteria')}</span></div></div>
            <div className={styles.statusColumn}><div className={`${styles.headerCell} ${styles.center}`}><span className={styles.headerText}>{t('table.valuation')}</span></div></div>
            <div className={styles.dateColumn}><div className={`${styles.headerCell} ${styles.center}`}><span className={styles.headerText}>{t('table.validationDate')}</span></div></div>
            <div className={styles.commentsColumn}><div className={styles.headerCell}><span className={styles.headerText}>{t('table.responsibleRole')}</span></div></div>
            <div className={styles.actionsColumn}><div className={`${styles.headerCell} ${styles.center}`}><span className={styles.headerText}>{t('table.actions')}</span></div></div>
          </div>

          {isLoading ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3 className={styles.emptyStateTitle}>{t('states.loading')}</h3>
                <p className={styles.emptyStateDescription}>{t('states.fetchingData')}</p>
              </div>
            </div>
          ) : tableData.length > 0 ? (
            filteredTableData.length > 0 ? (
              currentSectionData.map((section) => (
                <div key={section.id} className={styles.tableSection}>
                  <div className={styles.sectionRow}>
                    <div className={styles.idColumn}><div className={styles.sectionCell}><span className={styles.sectionCellText}>{section.items.length}</span></div></div>
                    <div className={styles.checkColumn}><div className={styles.sectionCell}><span className={styles.sectionCellText}></span></div></div>
                    <div className={styles.sectionTitleColumn}>
                      <div className={styles.sectionCell}>
                        <span className={styles.sectionTitle}>
                          {section.sprintName ?? section.deliverableName.replaceAll('_', ' ')}
                        </span>
                        {section.id.startsWith('deliverable-') && section.deliverableId && (
                            <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0 4px',
                                  color: '#6B7280',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                title={t('iconTitles.openDeliverable')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const deliverable = allDeliverables?.find((d: any) => d.id === section.deliverableId);
                                  if (deliverable) {
                                    setSelectedDeliverable(deliverable);
                                    setIsDeliverablePopupOpen(true);
                                  }
                                }}
                            >
                              <IconExternalLink size={14}/>
                            </button>
                        )}
                      </div>
                    </div>
                    <div className={styles.sectionOptionsColumn}>
                      <div className={styles.sectionCell}>
                        <div className={styles.chevronIconWrapper} onClick={() => toggleSection(section.id)}>
                          {expandedSections.includes(section.id) ? <IconChevronUp /> : <IconChevronDown />}
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedSections.includes(section.id) && section.items.filter(item => statusFilter.length > 0 ? true : item.status !== Status.FINISHED).map((item, itemIndex) => (
                    <div key={item.id} className={styles.dataRow} style={{ background: itemIndex % 2 === 1 ? '#F9FAFB' : '#FFFFFF', cursor: 'pointer', ...(showDropdown === item.id ? { zIndex: 10 } : {}) }} onClick={() => handleCheckpointClick(item)}>
                      <div className={styles.idColumn}><div className={`${styles.dataCell} ${styles.center}`}><span className={styles.dataCellText}>{item.checkpointId ? `CP-${item.checkpointId}` : 'No CP'}</span></div></div>
                      <div className={styles.checkColumn}><div className={`${styles.dataCell} ${styles.center}`}><span className={styles.dataCellText}>{`T-${item.taskId}`}</span></div></div>
                      <div className={styles.descriptionColumn}>
                        <div className={styles.dataCell}>
                          <span className={styles.dataCellText} title={item.taskName}>{item.taskName.replaceAll('_', ' ')}</span>
                        </div>
                      </div>
                      <div className={styles.deliveryColumn}><div className={styles.dataCell}><span className={styles.dataCellText} title={item.validationCriteria}>{item.validationCriteria || 'No criteria'}</span></div></div>
                      <div className={styles.deliveryColumn}><div className={styles.dataCell}><span className={styles.dataCellText} title={item.acceptanceCriteria}>{item.acceptanceCriteria || 'No criteria'}</span></div></div>
                      <div className={styles.statusColumn}>
                        <div className={`${styles.dataCell} ${styles.center}`}>
                          <div className={styles.valuationDots}>
                            <div
                              className={`${styles.valuationDot} ${styles.redDot} ${item.status === Status.DELAYED ? styles.active : styles.pale}`}
                              onClick={(e) => handleStatusDotClick(item, Status.DELAYED, e)}
                              title={t('iconTitles.delayed')}
                            />
                            <div
                              className={`${styles.valuationDot} ${styles.yellowDot} ${item.status === Status.PLANNED ? styles.active : styles.pale}`}
                              onClick={(e) => handleStatusDotClick(item, Status.PLANNED, e)}
                              title={t('iconTitles.planned')}
                            />
                            <div
                              className={`${styles.valuationDot} ${styles.greenDot} ${item.status === Status.FINISHED ? styles.active : styles.pale}`}
                              onClick={(e) => handleStatusDotClick(item, Status.FINISHED, e)}
                              title={t('iconTitles.finished')}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={styles.dateColumn}><div className={`${styles.dataCell} ${styles.center}`}><span className={styles.dataCellText} title={item.validationDate}>{item.validationDate}</span></div></div>
                      <div className={styles.commentsColumn}>
                        <div className={styles.dataCell}><span className={styles.dataCellText}
                                                               title={item.responsibleRole}>{item.responsibleRole ? (roleNameByKey.get(item.responsibleRole) || item.responsibleRole.replaceAll('_', ' ')) : t('states.notAssigned')}</span>
                        </div>
                      </div>
                      <div className={styles.actionsColumn}>
                        <div className={`${styles.dataCell} ${styles.center}`}>
                          <div className={styles.actionButtons}>
                            <div className={styles.actionDropdown}>
                              <button className={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleRowOptionsClick(item, e); }} title="More Options"><IconDots size={16} /></button>
                              {showDropdown === item.id && (
                                <div className={styles.dropdownMenu}>
                                  <button
                                    className={styles.dropdownItem}
                                    onClick={(e) => { e.stopPropagation(); setShowDropdown(null); setCheckpointForActionPoint(item); }}
                                  >
                                    <IconPlus size={16} />
                                    {t('actions.addActionPoint', 'Add Action Point')}
                                  </button>
                                  <button
                                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}
                                  >
                                    <IconTrash size={16} />
                                    {t('actions.delete', 'Delete')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateContent}>
                  <h3 className={styles.emptyStateTitle}>{t('states.noMatch')}</h3>
                  <p className={styles.emptyStateDescription}>{t('states.adjustFilters')}</p>
                </div>
              </div>
            )
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3 className={styles.emptyStateTitle}>{t('states.noData')}</h3>
                <p className={styles.emptyStateDescription}>{t('states.noCheckpoints')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {filteredTableData.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationLeft}>
            <span className={styles.itemsPerPageLabel}>Show:</span>
            <select value={itemsPerPage} onChange={(e) => handleItemsPerPageChange(Number(e.target.value))} className={styles.itemsPerPageSelect}>
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={8}>8</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
            <span className={styles.itemsPerPageLabel}>{t('pagination.perPage')}</span>
          </div>
          <span className={styles.paginationDetails}>{t('pagination.page', { current: currentPage, total: totalPages })}<span> • {t('pagination.showing', { from: startIndex + 1, to: Math.min(endIndex, filteredTableData.length), total: filteredTableData.length })}</span></span>
          <div className={styles.paginationRight}>
            <button className={styles.paginationButton} onClick={handlePrevPage} disabled={currentPage === 1}>{t('pagination.previous')}</button>
            <button className={styles.paginationButton} onClick={handleNextPage} disabled={currentPage === totalPages}>{t('pagination.next')}</button>
          </div>
        </div>
      )}

      {selectedCheckpoint && (
        <CheckpointDetailModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} checkpoint={selectedCheckpoint} onSave={handleSaveCheckpoint} />
      )}

      <CreateActionPointModal
        isOpen={!!checkpointForActionPoint}
        onClose={() => setCheckpointForActionPoint(null)}
        onActionPointCreated={() => { setCheckpointForActionPoint(null); queryClient.invalidateQueries({ queryKey: getGetActionpointProjectIdProjectQueryKey(project?.id) }); }}
        project={project}
        preselectedCheckpointId={checkpointForActionPoint?.checkpointId}
      />

      <CreateQualityCheckPlanModal
        isOpen={isQualityPlanModalOpen}
        onClose={() => setIsQualityPlanModalOpen(false)}
        onSave={handleCreateQualityPlan}
        project={project}
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('dialogs.deleteCheckpoint.title')}
        message="Are you sure you want to delete this checkpoint? This action cannot be undone."
        confirmText={t('dialogs.deleteCheckpoint.confirm')}
        cancelText={t('dialogs.deleteCheckpoint.cancel')}
        type="danger"
      />

      <ConfirmationDialog
        isOpen={isStatusChangeDialogOpen}
        onClose={() => setIsStatusChangeDialogOpen(false)}
        onConfirm={handleConfirmStatusChange}
        title={t('dialogs.changeStatus.title')}
        message={`Are you sure you want to change the valuation status to ${statusChangeData?.newStatus}?`}
        confirmText={t('dialogs.changeStatus.confirm')}
        cancelText={t('dialogs.deleteCheckpoint.cancel')}
        type="warning"
      />

      <ConfirmationDialog
        isOpen={isDeleteTabDialogOpen}
        onClose={() => {
          setIsDeleteTabDialogOpen(false);
          setTabToDelete(null);
        }}
        onConfirm={confirmDeleteTab}
        title={t('dialogs.deletePlan.title')}
        message={`Are you sure you want to delete "${tabToDelete?.name}"? This action cannot be undone.`}
        confirmText={t('dialogs.deletePlan.confirm')}
        cancelText={t('dialogs.deleteCheckpoint.cancel')}
        type="danger"
      />

      {selectedDeliverable && (
          <DeliverableDetailPopup
              isOpen={isDeliverablePopupOpen}
              onClose={() => {
                setIsDeliverablePopupOpen(false);
                setSelectedDeliverable(null);
              }}
              deliverable={selectedDeliverable}
              projectId={project?.id}
              project={project}
          />
      )}
    </>
  );
};

export default CheckPlan;
